/*
  Tuna Tubes configurator — the flow, the constraints and submit gating.

  THESIS: configuring a bank should feel like laying hardware on a bench, not
  filling in a form. Every choice is the object itself, drawn to true relative
  scale, and the arrangement step IS the bank — dragged into order. It refuses
  the category default: stacked radio groups under explanatory paragraphs.

  OWN-WORLD: the theme's light-steel ground and white plates, ocean navy
  #103357 for ink and selection, teal reserved for the CTA and for a legal drop
  target. Bebas for the tube number and the step title, Montserrat for
  everything else. One option shape — .ef-ttc__opt — carries mount, layout,
  height and size, so no two choices look like different species.

  STORY: pick where they mount, say how many, walk the tubes one at a time,
  drag them into the order you want, choose the finish, leave a number.

  FIRST VIEWPORT: preview left and sticky, the panel right; one question on
  screen at a time under six titled chips.

  FORM: the client's own order, 26 Aug 2026 — mount, count, per-tube walk,
  arrange, finish, details. Existing surface, existing visual world; no
  concept roll.

  ---------------------------------------------------------------------------

  THE RULES, from the client:
    - Height: Transom offers 17 3/4" and 22". Modular offers 15" and 17 3/4".
    - Size: Small exists only on a transom tube that is itself 17 3/4" tall.
    - Jumbo lives on the ends: a tube may be Jumbo only if every tube between
      it and the nearer end is Jumbo too. Per ROW in a 2xN square.
    - Square is a modular bank of 4 or 6 only.
    - Request order only after email + phone.

  THE BANK IS `bank`, AN ARRAY OF { h, w }. It is the single source of truth.
  `writeBank()` copies it into the hidden select pairs, which is the contract
  tuna-scene.js reads — `[data-key="slot"]` and `[data-key="slotheight"]` by
  DOCUMENT ORDER, index 0..n-1, port to starboard. The selects never move; only
  their values change, so reordering the rack cannot desynchronise the preview.

  DISPATCH CHANGE FROM A CONTROL, NOT THE FORM. Setting `select.value` in code
  fires nothing, and both this file and tuna-scene.js hang off one
  `form.addEventListener('change')`. Every programmatic write ends in
  `fireChange()`, which dispatches a bubbling event from a real select.

  Pricing is deliberately absent and stays that way — the client closed the
  matrix question. PRICE_MATRIX is null, estimateFor() returns null rather than
  a guess, and the estimate shows the section's pending text.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-ef-ttc]');
  if (!root) return;

  var form = root.querySelector('form');
  if (!form) return;

  /* ---- product truth ---------------------------------------------------- */

  var MOUNT_HEIGHTS = { transom: ['17.75', '22'], modular: ['15', '17.75'] };

  var MOUNT_INFO = {
    transom: 'Bolts across the stern. Offers 17¾″ and 22″ tubes.',
    modular: 'A free-standing rack. Offers 15″ and 17¾″ tubes, and can be built as a block.',
  };

  var HEIGHT_OPTS = [
    { v: '15',    label: '15″',   info: 'Modular racks only.' },
    { v: '17.75', label: '17¾″',  info: 'The common height. Fits either mount.' },
    { v: '22',    label: '22″',   info: 'Deepest submersion. Transom mounts only.' },
  ];

  var SIZES = [
    { v: 'small',  label: 'Small',  short: 'S',
      dims: '3 5/16″ × 4 3/4″', info: 'Compact live wells and smaller bait species.' },
    { v: 'medium', label: 'Medium', short: 'M',
      dims: '5″ × 6.5″',        info: 'Most popular. Suits most offshore baitfish up to 20 lbs.' },
    { v: 'jumbo',  label: 'Jumbo',  short: 'J',
      dims: '6″ × 8″',          info: 'Jumbo baits and deeper submersion.' },
  ];

  var MAX_SLOTS = 6;

  /* THE CLIENT'S PRICE MATRIX. Closed, not pending — he asked for "price on
     request" and no matrix. Everything below stays inert: estimateFor()
     returns null and the estimate shows the section's pending text. There is
     deliberately no fallback arithmetic, because a made-to-order price guessed
     from an incomplete matrix would reach a customer looking real.

     Shape, if that decision is ever reopened — keyed mount|height|width, value
     in AED for ONE tube; set PER_TUBE false and append |tubes to key a whole
     bank flat. */
  var PRICE_MATRIX = null;
  var PER_TUBE = true;
  var EXTRAS = {
    suckers: 0,      /* Sea Suckers add-on, flat */
    manifold: 0,     /* Manifold add-on, flat (transom only) */
    square: 0,       /* 2x2 / 2x3 rack over a line rack, flat */
    plugs: {},       /* per plug type, e.g. { hubbell: 0, cannon: 0 } */
  };

  /* ---- elements --------------------------------------------------------- */

  var estimateEl = root.querySelector('[data-ttc-estimate]');
  var estimateValueEl = root.querySelector('[data-ttc-estimate-value]');
  var estimateNoteEl = root.querySelector('[data-ttc-estimate-note]');
  var pendingText = estimateValueEl ? estimateValueEl.textContent.trim() : '';

  var submit = form.querySelector('[data-ttc-submit]');
  var hint = form.querySelector('[data-ttc-hint]');
  /* The summary lives beside the PREVIEW now, which is outside the form — so
     it is looked up from the section root, not the form. */
  var summaryEl = root.querySelector('[data-ttc-summary]');
  var costEl = root.querySelector('[data-ttc-cost]');
  var cname = form.querySelector('input[name="contact[name]"]');
  var email = form.querySelector('input[name="contact[email]"]');
  var phone = form.querySelector('input[name="contact[phone]"]');

  var modelBox = form.querySelector('[data-ttc-slots]');
  var slotField = form.querySelector('[data-ttc-slotfield]');
  var slotFieldH = form.querySelector('[data-ttc-slotfield-h]');

  var walkTitle = form.querySelector('[data-ttc-walk-title]');
  var pipsBox = form.querySelector('[data-ttc-pips]');
  var heightOptsBox = form.querySelector('[data-ttc-height-opts]');
  var sizeOptsBox = form.querySelector('[data-ttc-size-opts]');

  var layoutFs = form.querySelector('[data-ttc-layout-fs]');
  var rackBox = form.querySelector('[data-ttc-rack]');
  var arrangeMsg = form.querySelector('[data-ttc-arrange-msg]');

  var stepNav = form.querySelector('[data-ttc-stepnav]');
  var navBox = form.querySelector('[data-ttc-nav]');
  /* The progress bar doubles as the scroll target on a phone — see showStep. */
  var progressEl = root.querySelector('[data-ttc-progress]');
  var backBtn = form.querySelector('[data-ttc-back]');
  var nextBtn = form.querySelector('[data-ttc-next]');

  /* ---- money and the estimate ------------------------------------------- */

  /* Shopify money_format, so the figure matches the rest of the storefront
     instead of a hardcoded currency. Handles the standard tokens; an
     unrecognised format falls through to the amount with two decimals. */
  function formatMoney(amount) {
    var fmt = (estimateEl && estimateEl.getAttribute('data-money-format')) || '';

    function group(n, decimals, thousands, decimal) {
      var parts = Math.abs(n).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (n < 0 ? '-' : '') + parts.join(parts[1] ? decimal : '');
    }

    var TOKENS = {
      amount: function () { return group(amount, 2, ',', '.'); },
      amount_no_decimals: function () { return group(amount, 0, ',', '.'); },
      amount_with_comma_separator: function () { return group(amount, 2, '.', ','); },
      amount_no_decimals_with_comma_separator: function () { return group(amount, 0, '.', ','); },
      amount_with_space_separator: function () { return group(amount, 2, ' ', ','); },
      amount_no_decimals_with_space_separator: function () { return group(amount, 0, ' ', ','); },
      amount_with_apostrophe_separator: function () { return group(amount, 2, "'", '.'); },
    };

    var out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      return TOKENS[name] ? TOKENS[name]() : m;
    });
    /* No usable format string, or it carried no token we know. */
    if (!fmt || out === fmt) return TOKENS.amount();
    /* THIS SHOP'S money_format CARRIES MARKUP — a currency-converter app wraps
       it in <span class=cvc-money>. The figure is written with textContent (a
       shop setting is not a safe innerHTML source), so the tags have to come
       out here or they render as literal text. */
    return out.replace(/<[^>]*>/g, '').trim();
  }

  /* Returns a number in AED, or null when the matrix cannot answer. Null is
     the honest answer and is what the caller shows the pending text for —
     never a zero, and never a partial sum. */
  function estimateFor(state) {
    if (!PRICE_MATRIX) return null;
    if (!state.mount || !state.height || !state.width || !state.tubes) return null;

    var key = PER_TUBE
      ? [state.mount, state.height, state.width].join('|')
      : [state.mount, state.height, state.width, state.tubes].join('|');
    var base = PRICE_MATRIX[key];
    if (typeof base !== 'number') return null;

    var total = PER_TUBE ? base * state.tubes : base;
    if (state.suckers) total += EXTRAS.suckers || 0;
    if (state.manifold) total += EXTRAS.manifold || 0;
    if (state.layout === 'square') total += EXTRAS.square || 0;
    if (state.plugs && EXTRAS.plugs) total += EXTRAS.plugs[state.plugs] || 0;
    return total;
  }

  function renderEstimate() {
    if (!estimateValueEl) return;

    /* A mixed bank has no single height or width to key a per-tube matrix on;
       tube 1 is the representative, exactly as the old single radios were. */
    var amount = estimateFor({
      mount: checkedValue('mount'),
      height: bank.length ? bank[0].h : null,
      width: bank.length ? bank[0].w : null,
      tubes: tubeCount(),
      color: checkedValue('color'),
      plugs: checkedValue('plugs'),
      layout: effectiveLayout(),
      suckers: suckersOn(),
      manifold: manifoldOn(),
    });

    var priced = amount !== null;
    estimateValueEl.textContent = priced ? formatMoney(amount) : pendingText;
    /* The attribute drives the styling: prose while pending, a figure once
       there is one. Presence is what CSS keys on, so it is removed, not set to
       a falsy string. */
    if (priced) estimateValueEl.removeAttribute('data-pending');
    else estimateValueEl.setAttribute('data-pending', '');

    if (estimateNoteEl && estimateEl) {
      var note = estimateEl.getAttribute(priced ? 'data-priced-note' : 'data-pending-note');
      estimateNoteEl.textContent = note || '';
      estimateNoteEl.hidden = !note;
    }
  }

  /* ---- small readers ---------------------------------------------------- */

  function radios(key) {
    return Array.prototype.slice.call(
      form.querySelectorAll('input[type="radio"][data-key="' + key + '"]')
    );
  }

  function checkedValue(key) {
    var r = form.querySelector('input[type="radio"][data-key="' + key + '"]:checked');
    return r ? r.getAttribute('data-value') : null;
  }

  function checkedLabel(key) {
    var r = form.querySelector('input[type="radio"][data-key="' + key + '"]:checked');
    return r ? (r.getAttribute('data-label') || r.value) : null;
  }

  function setDisabled(input, off) {
    input.disabled = off;
    /* An illegal combo is never kept silently — clear it and let the gating
       report the group as unanswered. */
    if (off && input.checked) input.checked = false;
  }

  function suckersOn() {
    var s = form.querySelector('input[data-key="suckers"]');
    return !!(s && s.checked);
  }

  function manifoldOn() {
    var m = form.querySelector('input[data-key="manifold"]');
    return !!(m && m.checked && !m.disabled);
  }

  function mount() { return checkedValue('mount') || 'transom'; }
  function legalHeights() { return MOUNT_HEIGHTS[mount()] || MOUNT_HEIGHTS.transom; }
  function tubeCount() { return parseInt(checkedValue('tubes'), 10) || 0; }

  /* Square only exists on a modular 4 or 6 — a transom bank bolts along the
     stern in one line. Everything downstream asks this, never the radio. */
  function effectiveLayout() {
    var n = tubeCount();
    var square = checkedValue('layout') === 'square' &&
                 mount() === 'modular' && (n === 4 || n === 6);
    return square ? 'square' : 'line';
  }

  function squareAllowed() {
    var n = tubeCount();
    return mount() === 'modular' && (n === 4 || n === 6);
  }

  /* Small is judged on THIS tube's height, not the bank's — a neighbour being
     22 does not stop it. */
  function smallLegal(h) { return mount() === 'transom' && h === '17.75'; }

  function labelForHeight(v) {
    if (!v) return '—';
    for (var i = 0; i < HEIGHT_OPTS.length; i++) {
      if (HEIGHT_OPTS[i].v === v) return HEIGHT_OPTS[i].label;
    }
    return v;
  }

  function labelForSize(v) {
    if (!v) return '—';
    for (var i = 0; i < SIZES.length; i++) if (SIZES[i].v === v) return SIZES[i].label;
    return v;
  }

  function shortForSize(v) {
    if (!v) return '—';
    for (var i = 0; i < SIZES.length; i++) if (SIZES[i].v === v) return SIZES[i].short;
    return v;
  }

  /* ---- the bank --------------------------------------------------------- */

  var bank = [];          /* [{ h, w }], port to starboard. The source of truth. */
  var pairsH = [];        /* the hidden selects tuna-scene.js reads, by index   */
  var pairsW = [];
  var writing = false;    /* re-entrancy guard around programmatic change       */

  function buildModel() {
    if (!modelBox) return;
    var html = '';
    for (var i = 0; i < MAX_SLOTS; i++) {
      html += '<span data-ttc-pair>';
      html += '<select data-key="slotheight" tabindex="-1">';
      for (var a = 0; a < HEIGHT_OPTS.length; a++) {
        html += '<option value="' + HEIGHT_OPTS[a].v + '">' + HEIGHT_OPTS[a].label + '</option>';
      }
      html += '</select><select data-key="slot" tabindex="-1">';
      for (var b = 0; b < SIZES.length; b++) {
        html += '<option value="' + SIZES[b].v + '">' + SIZES[b].label + '</option>';
      }
      html += '</select></span>';
    }
    modelBox.innerHTML = html;
    pairsH = Array.prototype.slice.call(modelBox.querySelectorAll('[data-key="slotheight"]'));
    pairsW = Array.prototype.slice.call(modelBox.querySelectorAll('[data-key="slot"]'));
  }

  /* 17 3/4in is the common height and the only one BOTH mounts offer, so it is
     the default wherever it is legal — taking legalHeights()[0] instead would
     default a modular bank to 15in purely from array order, and switching
     mount would then silently re-height every tube. */
  function defaultHeight() {
    var legal = legalHeights();
    return legal.indexOf('17.75') !== -1 ? '17.75' : legal[0];
  }

  /* Grow, shrink and correct the bank against the current mount. New tubes
     take the default height and Medium — never Small, which is legal on so
     few banks that defaulting to it would usually be corrected away again. */
  /* NOTHING IS PRE-CHOSEN. A tube starts `{ h: null, w: null }` and the walk
     will not advance until both are answered — a highlighted button the
     customer never pressed reads as a decision they made, and on a
     made-to-order form that decision reaches the workshop. defaultHeight()
     survives only as what the PREVIEW draws for a tube not yet specified.

     Illegal values are CLEARED, never silently swapped: changing the mount
     out from under a 22in tube, or the height out from under a Small, sends
     the customer back to that button rather than picking for them. */
  function reconcileBank() {
    var n = tubeCount();
    var legal = legalHeights();

    while (bank.length < n) bank.push({ h: null, w: null });
    if (bank.length > n) bank.length = n;

    for (var i = 0; i < bank.length; i++) {
      if (bank[i].h !== null && legal.indexOf(bank[i].h) === -1) bank[i].h = null;
      if (bank[i].w === 'small' && !smallLegal(bank[i].h)) bank[i].w = null;
    }
    shareModularHeight();
  }

  /* A MODULAR bank is one height throughout (client, 28 Oct 2026): whatever
     Tube 1 is given, every tube gets. Transom keeps per-tube heights. Runs
     after every reconcile, so tubes added later and a switch from transom
     (where heights could differ) both fall in line with Tube 1. */
  function heightShared() { return mount() === 'modular'; }

  function shareModularHeight() {
    if (!heightShared() || !bank.length) return;
    for (var i = 1; i < bank.length; i++) bank[i].h = bank[0].h;
  }

  function tubeDone(t) { return !!t && t.h !== null && t.w !== null; }
  function bankDone() { return bank.length > 0 && bank.every(tubeDone); }

  /* Copy the bank into the selects the preview reads, then the two posted
     summary fields. Returns true when anything actually moved. */
  function writeBank() {
    var n = bank.length;
    var moved = false;
    for (var i = 0; i < MAX_SLOTS; i++) {
      /* An unspecified tube still has to draw, so the PREVIEW falls back —
         `bank` keeps the null that says the customer has not answered. */
      var h = (i < n && bank[i].h) || defaultHeight();
      var w = (i < n && bank[i].w) || 'medium';
      if (pairsH[i] && pairsH[i].value !== h) { pairsH[i].value = h; moved = true; }
      if (pairsW[i] && pairsW[i].value !== w) { pairsW[i].value = w; moved = true; }
    }
    /* Only once every tube is answered — a half-walked bank must not post a
       list with the preview's fallbacks standing in for real choices. */
    var done = bankDone();
    if (slotField) {
      slotField.value = done
        ? bank.map(function (t) { return labelForSize(t.w); }).join(', ') : '';
    }
    if (slotFieldH) {
      slotFieldH.value = done
        ? bank.map(function (t) { return labelForHeight(t.h); }).join(', ') : '';
    }
    return moved;
  }

  /* SETTING select.value IN CODE FIRES NOTHING. Dispatch from a control so it
     bubbles like a real interaction — the form's own listeners only read
     state, so there is no loop, but `writing` keeps a programmatic write from
     re-entering the correction pass. */
  function fireChange() {
    var src = pairsW[0] || form;
    writing = true;
    src.dispatchEvent(new Event('change', { bubbles: true }));
    writing = false;
  }

  /* The one call every user action ends in. */
  function commit() {
    reconcileBank();
    writeBank();
    fireChange();
    refresh();
  }

  /* ---- the jumbo-ends rule ---------------------------------------------- */

  /* Rows are lines with their own two ends: a 2x3 allows jumbos at 1, 3, 4, 6. */
  function rowRanges(n, layout) {
    if (layout !== 'square') return [[0, n - 1]];
    var cols = n / 2;
    return [[0, cols - 1], [cols, n - 1]];
  }

  /* A tube may be Jumbo if it has an unbroken run of Jumbos to EITHER end of
     its own row. Pure function of the arrangement — which is what lets the
     drag test a proposed order before accepting it. */
  function jumboAllowed(vals, n, layout) {
    var allow = new Array(n);
    rowRanges(n, layout).forEach(function (rg) {
      for (var i = rg[0]; i <= rg[1]; i++) {
        var leftOk = true, rightOk = true;
        for (var l = rg[0]; l < i; l++) if (vals[l] !== 'jumbo') { leftOk = false; break; }
        for (var r = i + 1; r <= rg[1]; r++) if (vals[r] !== 'jumbo') { rightOk = false; break; }
        allow[i] = leftOk || rightOk;
      }
    });
    return allow;
  }

  function arrangementLegal(arr, layout) {
    var vals = arr.map(function (t) { return t.w; });
    var allow = jumboAllowed(vals, arr.length, layout);
    for (var i = 0; i < arr.length; i++) if (vals[i] === 'jumbo' && !allow[i]) return false;
    return true;
  }

  /* Arriving at the arrange step with jumbos stranded in the middle, MOVE them
     to the ends rather than resizing them — the customer chose those sizes two
     steps ago and a silent demotion would throw the choice away. Within a row,
     jumbos fill inward from both ends and the rest keep their relative order. */
  function settleRow(arr, a, b) {
    var seg = arr.slice(a, b + 1);
    var jum = seg.filter(function (t) { return t.w === 'jumbo'; });
    var rest = seg.filter(function (t) { return t.w !== 'jumbo'; });
    if (!jum.length || !rest.length) return seg;
    var left = Math.ceil(jum.length / 2);
    return jum.slice(0, left).concat(rest, jum.slice(left));
  }

  function settleArrangement() {
    var layout = effectiveLayout();
    if (arrangementLegal(bank, layout)) return false;
    var out = [];
    rowRanges(bank.length, layout).forEach(function (rg) {
      out = out.concat(settleRow(bank, rg[0], rg[1]));
    });
    var changed = out.some(function (t, i) { return t !== bank[i]; });
    bank = out;
    return changed;
  }

  /* ---- the tube walk ---------------------------------------------------- */

  var walkAt = 0;         /* which tube the walk is showing */

  function optButton(opts) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ef-ttc__opt';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', opts.on ? 'true' : 'false');
    /* A disabled button is unreachable by keyboard and announces nothing, so
       the reason would be invisible to a screen reader. aria-disabled keeps it
       focusable and readable; the click handler declines the action. */
    if (opts.off) b.setAttribute('aria-disabled', 'true');
    b.tabIndex = opts.on && !opts.off ? 0 : -1;
    b.dataset.val = opts.val;
    b.textContent = opts.name;
    return b;
  }

  /* One line under the row, describing the option that is CURRENTLY CHOSEN.
     Every tile carrying its own paragraph is what made the old step a wall of
     text; the customer only needs the detail for the one they have landed on. */
  function setInfo(box, text) {
    var el = box.parentElement.querySelector('[data-ttc-info]');
    if (el) el.textContent = text || '';
  }

  /* Roving tabindex, so a radiogroup takes ONE tab stop and the arrows move
     within it — the native behaviour these buttons stand in for. */
  function wireGroup(box) {
    box.addEventListener('keydown', function (e) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      var items = Array.prototype.slice.call(box.querySelectorAll('.ef-ttc__opt'))
        .filter(function (el) { return el.getAttribute('aria-disabled') !== 'true'; });
      if (!items.length) return;
      var i = items.indexOf(document.activeElement);
      var to;
      if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = items.length - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (i + 1) % items.length;
      else to = (i - 1 + items.length) % items.length;
      e.preventDefault();
      items[to].focus();
      items[to].click();
    });
  }

  /* Small exists only on a transom tube that is itself 17 3/4in tall, so it is
     unavailable for two quite different reasons and the customer deserves the
     one that applies to them. */
  function whySmallOff(h) {
    if (mount() !== 'transom') return 'Small is transom-mount only.';
    if (h === null) return 'Choose a height first — Small needs a 17¾″ tube.';
    return 'Small needs a 17¾″ tube.';
  }

  function renderWalk() {
    var n = tubeCount();
    if (!n || !heightOptsBox || !sizeOptsBox) return;
    if (walkAt >= n) walkAt = n - 1;
    var tube = bank[walkAt];
    if (!tube) return;

    if (walkTitle) walkTitle.textContent = 'Tube ' + (walkAt + 1);

    if (pipsBox) {
      var pips = '';
      for (var p = 0; p < n; p++) {
        pips += '<li class="ef-ttc__pip"' +
                (p === walkAt ? ' data-now' : (p < walkAt ? ' data-done' : '')) + '></li>';
      }
      pipsBox.innerHTML = pips;
    }

    /* Height: only the two this mount offers are drawn at all. An option that
       cannot apply is never rendered as a control that looks live and does
       nothing — which is exactly how the old set-all buttons failed. */
    /* Modular: the height row is asked once, on Tube 1, and hidden for the
       rest. Shown again on a later tube only if Tube 1 somehow has none, so
       the walk can never stall on a question it is not showing. */
    var heightFs = heightOptsBox.closest('fieldset');
    var askHeight = !heightShared() || walkAt === 0 || bank[0].h === null;
    if (heightFs) heightFs.hidden = !askHeight;

    var legal = legalHeights();
    heightOptsBox.innerHTML = '';
    HEIGHT_OPTS.forEach(function (h) {
      if (legal.indexOf(h.v) === -1) return;
      var b = optButton({ val: h.v, on: tube.h === h.v, off: false, name: h.label });
      b.addEventListener('click', function () {
        if (bank[walkAt].h === h.v) return;
        bank[walkAt].h = h.v;
        if (heightShared()) {
          bank.forEach(function (t) { t.h = h.v; });
        }
        /* Small only survives on a 17¾ transom tube. Correcting it here, on
           the tube the customer is looking at, is visible; leaving it to a
           later sweep is not. */
        if (bank[walkAt].w === 'small' && !smallLegal(h.v)) bank[walkAt].w = null;
        commit();
        /* commit() writes the model and repaints the preview; the BUTTONS are
           a separate view. Without this the old option keeps `aria-checked`
           and the new one never gets it, so the row shows the wrong choice —
           and changing the height has to re-gate Small in the row below. */
        renderWalk();
      });
      heightOptsBox.appendChild(b);
      if (tube.h === h.v) {
        setInfo(heightOptsBox, h.info + (heightShared() && n > 1
          ? ' Every tube on a modular bank is this height.' : ''));
      }
    });

    /* Size: all three always offered, because Small's availability is a fact
       about THIS tube worth stating rather than hiding. */
    sizeOptsBox.innerHTML = '';
    SIZES.forEach(function (sz) {
      var off = sz.v === 'small' && !smallLegal(tube.h);
      var b = optButton({ val: sz.v, on: tube.w === sz.v, off: off, name: sz.label });
      b.addEventListener('click', function () {
        /* A refused option says why, the same way a refused drop does on the
           rack — silence on a control that is visibly there reads as broken. */
        if (off) { setInfo(sizeOptsBox, whySmallOff(bank[walkAt].h)); return; }
        if (bank[walkAt].w === sz.v) return;
        bank[walkAt].w = sz.v;
        commit();
        renderWalk();
      });
      sizeOptsBox.appendChild(b);
      if (tube.w === sz.v) {
        setInfo(sizeOptsBox, sz.dims + ' · ' + sz.info);
      }
    });
    var smallBtn = sizeOptsBox.querySelector('[data-val="small"]');
    if (!smallLegal(tube.h) && smallBtn) smallBtn.title = whySmallOff(tube.h);

    /* A radiogroup with nothing chosen still has to be reachable: roving
       tabindex parks the tab stop on the checked option, and until there is
       one that would leave the whole row unfocusable. */
    [heightOptsBox, sizeOptsBox].forEach(function (box) {
      if (box.querySelector('[aria-checked="true"]')) return;
      var first = box.querySelector('.ef-ttc__opt:not([aria-disabled="true"])');
      if (first) first.tabIndex = 0;
    });
  }

  if (heightOptsBox) wireGroup(heightOptsBox);
  if (sizeOptsBox) wireGroup(sizeOptsBox);

  /* ---- the rack: drag to arrange ---------------------------------------- */

  var dragFrom = -1;
  var dragTo = -1;
  var dragEl = null;
  var dragX = 0, dragY = 0;

  function say(msg, tone) {
    if (!arrangeMsg) return;
    arrangeMsg.textContent = msg || '';
    if (tone) arrangeMsg.setAttribute('data-tone', tone);
    else arrangeMsg.removeAttribute('data-tone');
  }

  /* The move the drag is proposing, as an array — remove from `from`, insert
     at `to`. Tested for legality BEFORE the drop is accepted. */
  function moved(from, to) {
    var out = bank.slice();
    var t = out.splice(from, 1)[0];
    out.splice(to, 0, t);
    return out;
  }

  function canMove(from, to) {
    if (from === to) return true;
    return arrangementLegal(moved(from, to), effectiveLayout());
  }

  function renderRack() {
    if (!rackBox) return;
    var n = bank.length;
    var layout = effectiveLayout();
    rackBox.innerHTML = '';
    if (!n) return;

    rowRanges(n, layout).forEach(function (rg) {
      var row = document.createElement('div');
      row.className = 'ef-ttc__rackrow';
      for (var i = rg[0]; i <= rg[1]; i++) {
        (function (idx) {
          var t = bank[idx];
          var el = document.createElement('button');
          el.type = 'button';
          el.className = 'ef-ttc__tube';
          el.dataset.idx = idx;
          el.setAttribute('aria-label',
            'Tube ' + (idx + 1) + ', ' + labelForHeight(t.h) + ' ' + labelForSize(t.w) +
            '. Use the left and right arrow keys to move it.');
          /* A plain upright rectangle carrying the two facts that identify the
             tube: its size initial and its height. Nothing else — this is a
             thing to pick up and put down, not a place to read specifications. */
          el.innerHTML =
            '<span class="ef-ttc__tube-pos">' + (idx + 1) + '</span>' +
            '<span class="ef-ttc__tube-size">' + shortForSize(t.w) + '</span>' +
            '<span class="ef-ttc__tube-h">' + labelForHeight(t.h) + '</span>';
          row.appendChild(el);
        })(i);
      }
      rackBox.appendChild(row);
    });
  }

  function tubeElAt(x, y) {
    var el = document.elementFromPoint(x, y);
    return el ? el.closest('.ef-ttc__tube') : null;
  }

  function clearMarks() {
    Array.prototype.forEach.call(rackBox.querySelectorAll('.ef-ttc__tube'), function (el) {
      el.removeAttribute('data-lift');
      el.removeAttribute('data-drop');
      el.removeAttribute('data-shut');
    });
    rackBox.removeAttribute('data-dragging');
  }

  /* Close every position this tube may not take, BEFORE the drop — the
     refusal has to be visible while the tube is in the air, not arrive as a
     silent resize after it lands. */
  function markTargets(from) {
    Array.prototype.forEach.call(rackBox.querySelectorAll('.ef-ttc__tube'), function (el) {
      var idx = parseInt(el.dataset.idx, 10);
      if (idx === from) { el.setAttribute('data-lift', ''); return; }
      if (!canMove(from, idx)) el.setAttribute('data-shut', '');
    });
    rackBox.setAttribute('data-dragging', '');
  }

  if (rackBox) {
    rackBox.addEventListener('pointerdown', function (e) {
      var el = e.target.closest('.ef-ttc__tube');
      if (!el || e.button > 0) return;
      dragFrom = parseInt(el.dataset.idx, 10);
      dragTo = dragFrom;
      dragEl = el;
      dragX = e.clientX;
      dragY = e.clientY;
      /* Marks first: capture is a nicety that throws on a pointer the browser
         no longer considers active, and losing it must not cost the drag. */
      markTargets(dragFrom);
      try { el.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
      say('');
    });

    rackBox.addEventListener('pointermove', function (e) {
      if (dragFrom < 0) return;
      e.preventDefault();
      /* The tube follows the finger. `data-lift` also drops it out of hit
         testing, so elementFromPoint below reports the tube UNDERNEATH rather
         than the one being carried; pointer capture keeps delivering these
         events to it regardless. */
      if (dragEl) {
        dragEl.style.transform =
          'translate(' + (e.clientX - dragX) + 'px,' + (e.clientY - dragY) + 'px)';
      }
      var over = tubeElAt(e.clientX, e.clientY);
      Array.prototype.forEach.call(rackBox.querySelectorAll('[data-drop]'), function (el) {
        el.removeAttribute('data-drop');
      });
      if (!over) { dragTo = dragFrom; return; }
      var idx = parseInt(over.dataset.idx, 10);
      if (idx === dragFrom || !canMove(dragFrom, idx)) { dragTo = dragFrom; return; }
      dragTo = idx;
      over.setAttribute('data-drop', '');
    });

    function endDrag(e) {
      if (dragFrom < 0) return;
      var from = dragFrom, to = dragTo;
      dragFrom = -1; dragTo = -1;
      if (dragEl) { dragEl.style.transform = ''; dragEl = null; }
      clearMarks();
      if (to === from) {
        /* A refused drop, not a click: the tube was carried somewhere it
           cannot go. Say why rather than leaving it looking broken. */
        var over = e && e.clientX != null ? tubeElAt(e.clientX, e.clientY) : null;
        if (over && parseInt(over.dataset.idx, 10) !== from) {
          say('Jumbo tubes sit on the ends of the bank and fill inwards.', 'block');
        }
        renderRack();
        return;
      }
      bank = moved(from, to);
      commit();
      /* commit() writes the model and repaints the preview; the RACK is a
         separate view and has to be rebuilt or the rectangles stay where they
         were while everything else moved. */
      renderRack();
      say('Tube moved to position ' + (to + 1) + '.');
    }

    rackBox.addEventListener('pointerup', endDrag);
    rackBox.addEventListener('pointercancel', endDrag);

    /* DRAGGING CANNOT BE THE ONLY WAY IN. Same legality test, same commit. */
    rackBox.addEventListener('keydown', function (e) {
      var el = e.target.closest('.ef-ttc__tube');
      if (!el) return;
      var from = parseInt(el.dataset.idx, 10);
      var to = null;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = from - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = from + 1;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = bank.length - 1;
      else return;
      e.preventDefault();
      if (to < 0 || to > bank.length - 1) return;
      if (!canMove(from, to)) {
        say('Jumbo tubes sit on the ends of the bank and fill inwards.', 'block');
        return;
      }
      bank = moved(from, to);
      commit();
      renderRack();
      say('Tube moved to position ' + (to + 1) + '.');
      /* renderRack() replaced the node that had focus — follow the tube. */
      var next = rackBox.querySelector('.ef-ttc__tube[data-idx="' + to + '"]');
      if (next) next.focus();
    });
  }

  /* ---- the summary, the gate and the hint -------------------------------- */

  /* Has the customer actually been ASKED this yet? Several controls carry a
     default from the first paint — layout is Line before the arrange step
     exists — and reading a decision back before it was offered is a summary
     reporting the customer's own answers to them wrongly. */
  function reachedStep(k) {
    var trip = itinerary();
    var upto = Math.min(reached, trip.length - 1);
    for (var i = 0; i <= upto; i++) if (trip[i].k === k) return true;
    return false;
  }

  function summaryText() {
    var parts = [];
    var m = checkedLabel('mount');
    if (m) parts.push(m);

    var n = tubeCount();
    if (n && reachedStep('tubes')) parts.push(n + ' tube' + (n > 1 ? 's' : ''));

    /* One phrase per TUBE, not two parallel lists. A mixed bank read back as
       "22in + 22in + 17 3/4in ... " next to "Jumbo + Jumbo + Medium ..." makes
       the reader zip the two together to find out what tube 3 actually is. */
    if (bankDone() && reachedStep('tube')) {
      var specs = bank.map(function (t) {
        return labelForHeight(t.h) + ' ' + labelForSize(t.w);
      });
      var uniform = specs.every(function (x) { return x === specs[0]; });
      parts.push(uniform ? specs[0] : specs.join(' + '));
    }

    /* Only where it was a real choice: a transom bank is always a line and
       never sees the control, so "Line layout" would name a decision that was
       never on screen. The posted field still carries it either way. */
    var lay = checkedLabel('layout');
    if (lay && reachedStep('layout') && squareAllowed()) parts.push(lay);

    if (reachedStep('finish')) {
      var col = checkedLabel('color');
      if (col) parts.push(col);
      var pl = checkedLabel('plugs');
      if (pl) parts.push(pl);
      var cab = checkedLabel('cable');
      if (cab) parts.push(cab);
      var volts = checkedLabel('volts');
      if (volts) parts.push(volts);
      if (manifoldOn()) parts.push('Manifold');
      if (suckersOn()) parts.push('Sea Suckers');
    }
    return parts.join(' · ');
  }

  /* Required like the other two — this is a quote request, and a price goes back
     to a person. Trimmed, so a space bar does not pass. */
  function nameOk() { return cname && cname.value.trim() !== ''; }
  function phoneOk() { return phone && phone.value.replace(/\D/g, '').length >= 7; }
  function emailOk() { return email && email.value !== '' && email.validity.valid; }

  var GROUPS = ['mount', 'tubes', 'layout', 'color', 'plugs'];
  var LABELS = {
    mount: 'mount',
    tubes: 'number of tubes',
    layout: 'configuration',
    color: 'colour',
    plugs: 'plugs',
  };

  function allMissing() {
    var out = GROUPS.filter(function (k) {
      if (k === 'plugs' && mount() === 'transom') return false;
      return !form.querySelector('input[data-key="' + k + '"]:checked');
    }).map(function (k) { return LABELS[k]; });
    if (!nameOk()) out.push('name');
    if (!emailOk()) out.push('email');
    if (!phoneOk()) out.push('phone number');
    return out;
  }

  /* ---- the stepped flow -------------------------------------------------- */

  /* Six titled chips whatever the tube count, because the walk is ONE step
     visited N times — a nav that grew to eleven chips on a six-tube bank would
     reflow every time the count changed. */
  var STEP_KEYS = ['mount', 'tubes', 'tube', 'layout', 'finish', 'details'];
  var stepEls = {};
  STEP_KEYS.forEach(function (k) {
    stepEls[k] = form.querySelector('[data-ttc-step="' + k + '"]');
  });

  var pos = 0;            /* index into the itinerary */
  var reached = 0;        /* furthest itinerary index visited */

  /* mount, count, then one stop per tube, then arrange, finish, details. */
  function itinerary() {
    var out = [{ k: 'mount' }, { k: 'tubes' }];
    var n = tubeCount();
    for (var i = 0; i < n; i++) out.push({ k: 'tube', t: i });
    out.push({ k: 'layout' }, { k: 'finish' }, { k: 'details' });
    return out;
  }

  function stepMissing(k) {
    if (k === 'mount') return checkedValue('mount') ? [] : ['mount'];
    if (k === 'tubes') return tubeCount() ? [] : ['number of tubes'];
    if (k === 'tube') {
      var t = bank[walkAt];
      var need = [];
      if (!t || t.h === null) need.push('height');
      if (!t || t.w === null) need.push('size');
      return need;
    }
    if (k === 'layout') return checkedValue('layout') ? [] : ['configuration'];
    if (k === 'finish') {
      var out = [];
      if (!checkedValue('color')) out.push('colour');
      /* Plugs and cable are modular-only; a transom bank takes a manifold. */
      var modular = mount() !== 'transom';
      if (modular && !checkedValue('plugs')) out.push('plugs');
      /* Added with the controls themselves. Leaving them out here would let
         the walk complete with two of the fourteen posted fields blank, and
         the request would reach the shop looking finished. */
      if (modular && !checkedValue('cable')) out.push('cable length');
      if (modular && !checkedValue('volts')) out.push('voltage');
      return out;
    }
    var d = [];
    if (!nameOk()) d.push('name');
    if (!emailOk()) d.push('email');
    if (!phoneOk()) d.push('phone number');
    return d;
  }

  function showStep(i) {
    var trip = itinerary();
    if (i < 0) i = 0;
    if (i > trip.length - 1) i = trip.length - 1;
    var stop = trip[i];
    var last = i === trip.length - 1;

    /* Arriving at the arrange step, put any stranded jumbo on an end rather
       than resizing it. Only speak when something actually moved. */
    if (stop.k === 'layout') {
      if (settleArrangement()) {
        writeBank();
        fireChange();
        say('Jumbos moved to the ends — drag to rearrange.');
      } else {
        say('');
      }
    }

    pos = i;
    if (i > reached) reached = i;
    if (stop.k === 'tube') walkAt = stop.t;

    /* WHICH STOP WE ARE AT, published to CSS. A phone cannot afford to carry
       the section heading, its intro and the written read-back through all six
       stops — together they are ~226px of a screen that has to hold the
       preview as well. The styles hide them by stop rather than by scroll
       position, which is the only thing that actually knows.

       Set here and nowhere else, so it cannot disagree with what is on screen.
       Absent until the script runs, and every rule that uses it requires the
       attribute to be present — so with no JS nothing is hidden. */
    root.setAttribute('data-ttc-at', stop.k);

    /* A new stop starts clean — including the next tube on the walk, which
       reuses the same two fieldsets. */
    clearErrs();

    STEP_KEYS.forEach(function (k) {
      if (stepEls[k]) stepEls[k].hidden = k !== stop.k;
    });

    if (stop.k === 'tube') renderWalk();
    if (stop.k === 'layout') { renderRack(); syncLayoutRow(); }

    if (backBtn) backBtn.hidden = i === 0;
    if (nextBtn) nextBtn.hidden = last;
    var wrap = form.querySelector('.ef-ttc__submit-wrap');
    if (wrap) wrap.hidden = !last;
    if (costEl) costEl.hidden = !last;

    buildStepNav();
    refresh();

    /* Where a step LANDS.

       On a phone the preview is pinned to the top of the viewport, so putting
       the top of the section back on screen — which is the right answer on a
       desktop, where the preview sits beside the form — would park the new
       step below the fold behind a band that exists precisely to stop that.
       Aim at the progress bar instead: the wizard then arrives directly under
       the preview, with the step and its buttons beneath it.

       The breakpoint is NOT repeated here. The CSS flips `--ttc-stuck` to 1 in
       the same media query that pins the band, and this reads it back, so the
       two cannot drift apart. */
    if (root.getBoundingClientRect().top < 0) {
      var pinned =
        getComputedStyle(root).getPropertyValue('--ttc-stuck').trim() === '1';
      var mark = pinned && progressEl && !progressEl.hidden ? progressEl : root;
      mark.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function buildStepNav() {
    if (!stepNav) return;
    var trip = itinerary();
    var here = trip[Math.min(pos, trip.length - 1)].k;
    var reachedKeys = {};
    for (var r = 0; r <= Math.min(reached, trip.length - 1); r++) reachedKeys[trip[r].k] = true;

    var idxHere = STEP_KEYS.indexOf(here);
    var nameEl = root.querySelector('[data-ttc-progress-name]');
    var countEl = root.querySelector('[data-ttc-progress-count]');
    var hereEl = stepEls[here];
    if (nameEl && hereEl) nameEl.textContent = hereEl.getAttribute('data-step-title') || here;
    if (countEl) countEl.textContent = (idxHere + 1) + ' / ' + STEP_KEYS.length;

    stepNav.innerHTML = '';
    STEP_KEYS.forEach(function (k, i) {
      var el = stepEls[k];
      if (!el) return;
      var title = el.getAttribute('data-step-title') || k;
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ef-ttc__seg';
      /* The segment is a bar, so its accessible name has to be written out. */
      b.setAttribute('aria-label', 'Step ' + (i + 1) + ' of ' + STEP_KEYS.length + ': ' + title);
      if (k === here) b.setAttribute('aria-current', 'step');
      else if (i < idxHere) b.setAttribute('data-done', '');
      /* Later steps depend on earlier answers, so the bar never jumps forward
         past the furthest stop reached. */
      if (!reachedKeys[k]) b.disabled = true;
      b.addEventListener('click', function () {
        for (var j = 0; j < trip.length; j++) if (trip[j].k === k) return showStep(j);
      });
      li.appendChild(b);
      stepNav.appendChild(li);
    });

    if (progressEl) progressEl.hidden = false;
    if (navBox) navBox.hidden = false;
  }

  /* Square is offered only where it exists, and cleared — never silently
     kept — when a change makes it illegal. The value and label are rewritten
     from the live count so the request email names the real block. */
  function syncLayoutRow() {
    var n = tubeCount();
    var ok = squareAllowed();
    var grid = n === 6 ? '2×3' : '2×2';
    radios('layout').forEach(function (r) {
      if (r.getAttribute('data-value') !== 'square') return;
      setDisabled(r, !ok);
      r.value = 'Square (' + grid + ')';
      r.setAttribute('data-label', 'Square (' + grid + ')');
    });
    /* The shape lives in the word rather than in a diagram beside it. */
    var sqLabel = form.querySelector('[data-ttc-square-label]');
    if (sqLabel) sqLabel.textContent = ok ? 'Square ' + grid : 'Square';
    if (layoutFs) layoutFs.hidden = !ok;
    if (!ok) {
      var line = radios('layout').filter(function (r) {
        return r.getAttribute('data-value') === 'line';
      })[0];
      if (line && !line.checked) line.checked = true;
    }
  }

  /* Plugs, cable length and voltage belong to a modular bank; a transom bank is fed by
     a manifold, offered as an optional add-on instead. The losing side is
     hidden AND disabled — disabled inputs are skipped by validation and left
     out of the posted request — and setDisabled clears any choice it held. */
  function syncMountExtras() {
    var transom = mount() === 'transom';
    Array.prototype.forEach.call(form.querySelectorAll('[data-ttc-modular-only]'), function (fs) {
      fs.hidden = transom;
      if (transom && fs.hasAttribute('data-invalid')) setFsErr(fs, fsKey(fs), false);
      Array.prototype.forEach.call(fs.querySelectorAll('input'), function (i) { setDisabled(i, transom); });
    });
    Array.prototype.forEach.call(form.querySelectorAll('[data-ttc-transom-only]'), function (el) {
      el.hidden = !transom;
      Array.prototype.forEach.call(el.querySelectorAll('input'), function (i) { setDisabled(i, !transom); });
    });
  }

  /* Everything that only READS state. Never writes the bank, so it is safe to
     call from the form's own change handler. */
  function refresh() {
    var mi = form.querySelector('[data-ttc-mount-info]');
    if (mi) mi.textContent = MOUNT_INFO[mount()] || '';

    var trip = itinerary();
    var stop = trip[Math.min(pos, trip.length - 1)];
    var last = pos === trip.length - 1;

    if (summaryEl) {
      var txt = summaryText();
      summaryEl.textContent = txt;
      summaryEl.parentElement.hidden = !txt;
    }

    renderEstimate();

    var missing = allMissing();
    if (submit) submit.disabled = missing.length > 0;
    if (nextBtn) nextBtn.disabled = false;

    /* The hint speaks for the step on screen. Listing "colour, plugs, email"
       while the customer is on step 1 reads as a wall of homework for
       questions they have not been asked yet. */
    var shown = last ? missing : stepMissing(stop.k);
    if (hint) {
      hint.textContent = shown.length
        ? 'Still needed: ' + shown.join(', ')
        : (last ? 'Ready to send — we will contact you to confirm.' : 'Looks good — next.');
      hint.classList.toggle('ef-ttc__hint--ready', shown.length === 0);
    }
  }

  /* ---- per-question errors ---------------------------------------------

     The step hint at the foot of the form already said "Still needed: …", but
     the client found that customers pressed Next, nothing happened, and they
     could not see at a glance WHICH question was unanswered (7 Oct 2026). So
     Next now marks every unanswered question on the stop with its own red line
     under it, and scrolls the first one into view. Each line clears the moment
     that question is answered.

     A question is identified by what it holds: a radio with a `data-key`, or
     the walk's height/size rows, which are buttons over the bank rather than
     radios and so are judged from `bank[walkAt]`. The Add-on checkbox is
     optional and the Details fields are text, so neither is ever marked. */
  var ERR_TEXT = {
    mount: 'Please choose where they mount.',
    tubes: 'Please select the number of tubes first.',
    height: 'Please choose a height.',
    size: 'Please choose a size.',
    layout: 'Please choose a configuration.',
    color: 'Please choose a colour.',
    plugs: 'Please choose your plugs.',
    cable: 'Please choose a cable length.',
    volts: 'Please choose a voltage.'
  };

  function fsKey(fs) {
    if (fs.querySelector('[data-ttc-height-opts]')) return 'height';
    if (fs.querySelector('[data-ttc-size-opts]')) return 'size';
    var r = fs.querySelector('input[type="radio"][data-key]');
    return r ? r.getAttribute('data-key') : null;
  }

  function fsMissing(fs, k) {
    if (k === 'height' || k === 'size') {
      var t = bank[walkAt];
      return !t || (k === 'height' ? t.h : t.w) === null;
    }
    return !checkedValue(k);
  }

  function setFsErr(fs, k, on) {
    var p = fs.querySelector('.ef-ttc__err');
    if (!p && on) {
      p = document.createElement('p');
      p.className = 'ef-ttc__err';
      p.setAttribute('role', 'alert');
      fs.appendChild(p);
    }
    if (p) {
      if (on) p.textContent = ERR_TEXT[k] || 'Please choose an option.';
      p.hidden = !on;
    }
    fs.toggleAttribute('data-invalid', on);
  }

  /* Returns the first unanswered question so Next can bring it into view. */
  function markStep(k) {
    var el = stepEls[k], first = null;
    if (!el) return null;
    Array.prototype.forEach.call(el.querySelectorAll('fieldset'), function (fs) {
      if (fs.hidden) return;
      var key = fsKey(fs);
      if (!key) return;
      var miss = fsMissing(fs, key);
      setFsErr(fs, key, miss);
      if (miss && !first) first = fs;
    });
    return first;
  }

  /* Re-judge only the questions already marked, so answering one clears its
     line without marking questions the customer has not reached yet. */
  function recheckErrs() {
    Array.prototype.forEach.call(form.querySelectorAll('fieldset[data-invalid]'), function (fs) {
      var key = fsKey(fs);
      if (key) setFsErr(fs, key, fsMissing(fs, key));
    });
  }

  function clearErrs() {
    Array.prototype.forEach.call(form.querySelectorAll('fieldset[data-invalid]'), function (fs) {
      setFsErr(fs, fsKey(fs), false);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', function () {
      var trip = itinerary();
      var stop = trip[pos];
      var miss = stepMissing(stop.k);
      if (miss.length) {
        if (hint) {
          hint.textContent = 'Still needed: ' + miss.join(', ');
          hint.classList.remove('ef-ttc__hint--ready');
        }
        var first = markStep(stop.k);
        /* Bring it on screen only if it is not. On a phone the preview band
           is pinned over the top ~190px, so "on screen" starts below it. */
        if (first) {
          var r = first.getBoundingClientRect();
          var top = window.innerWidth < 990 ? 200 : 0;
          if (r.top < top || r.top > window.innerHeight - 80) {
            first.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        return;
      }
      showStep(pos + 1);
    });
  }

  if (backBtn) backBtn.addEventListener('click', function () { showStep(pos - 1); });

  /* ---- wiring ----------------------------------------------------------- */

  buildModel();
  reconcileBank();
  writeBank();
  syncLayoutRow();
  syncMountExtras();

  form.addEventListener('change', function (e) {
    /* A programmatic write is already consistent — re-running the correction
       pass from inside it is how a change loop starts. */
    /* Read-only, so it runs even for programmatic writes — picking a height
       arrives as one of those, and its error line must still clear. */
    recheckErrs();
    if (writing) return;

    var key = e.target && e.target.getAttribute && e.target.getAttribute('data-key');

    /* Mount changes the legal heights; the count changes the bank's length.
       Both have to reach the preview, so they end in a dispatch of their own
       rather than relying on the event already in flight. */
    if (key === 'mount' || key === 'tubes') {
      reconcileBank();
      syncLayoutRow();
      syncMountExtras();
      if (writeBank()) fireChange();
      if (tubeCount() && walkAt >= tubeCount()) walkAt = tubeCount() - 1;
      var trip = itinerary();
      if (pos > trip.length - 1) pos = trip.length - 1;
      if (reached > trip.length - 1) reached = trip.length - 1;
      if (trip[pos].k === 'tube') renderWalk();
      if (trip[pos].k === 'layout') { renderRack(); syncLayoutRow(); }
      buildStepNav();
    }

    if (key === 'layout') {
      /* A square splits one line into two rows, each with its own ends, so an
         arrangement legal as a line can be illegal as a block. */
      if (settleArrangement()) { writeBank(); fireChange(); }
      renderRack();
    }

    refresh();
  });

  ['input', 'blur'].forEach(function (ev) {
    if (cname) cname.addEventListener(ev, refresh);
    if (email) email.addEventListener(ev, refresh);
    if (phone) phone.addEventListener(ev, refresh);
  });

  /* ---- the request reference -------------------------------------------

     Shopify's contact form has no request number and sends the customer
     nothing, so this is generated in the browser: TT-YYMMDD-NNNN, stamped into
     a hidden field at submit time and stashed so the success screen — which is
     a fresh page load after the redirect — can read it back.

     A REFERENCE, NOT A UNIQUE ID. Four digits scoped to a date give 10,000 a
     day; at this shop's volume a clash is remote but not impossible. If two
     requests ever share one, the build details in the email still tell them
     apart. Guaranteed-unique numbering needs a backend.

     sessionStorage, not localStorage: it should die with the tab, so a later
     visit never shows a stale reference as if it were a new one. Wrapped
     because Safari's private mode throws on write. */
  var REF_KEY = 'ef-ttc-ref';

  function makeRef() {
    var d = new Date();
    var ymd = String(d.getFullYear()).slice(2) +
              ('0' + (d.getMonth() + 1)).slice(-2) +
              ('0' + d.getDate()).slice(-2);
    var n = String(Math.floor(Math.random() * 10000));
    return 'TT-' + ymd + '-' + ('000' + n).slice(-4);
  }

  var refField = form.querySelector('[data-ttc-ref]');

  form.addEventListener('submit', function () {
    if (!refField) return;
    var ref = makeRef();
    refField.value = ref;
    try { sessionStorage.setItem(REF_KEY, ref); } catch (e) {}
  });

  /* Read it back on the success screen, then drop it — the reference belongs to
     the request that was just sent, not to the next one. */
  (function showRef() {
    var out = root.querySelector('[data-ttc-ref-out]');
    if (!out) return;                       /* not the success state */
    var ref = null;
    try { ref = sessionStorage.getItem(REF_KEY); } catch (e) {}
    if (!ref) return;                       /* leave it hidden */
    var val = out.querySelector('[data-ttc-ref-val]');
    if (val) val.textContent = ref;
    out.hidden = false;
    try { sessionStorage.removeItem(REF_KEY); } catch (e) {}
  })();

  /* ---- clear the success flag out of the URL ----------------------------

     Shopify's contact form redirects back with `?contact_posted=true`, and
     `form.posted_successfully?` is rendered from that param — so the flash
     re-appears on every refresh, on Back, and for anyone the URL is sent to.

     On the plain contact page that is DELIBERATE and documented in
     contact-form.liquid: the message survives with JS off. It is wrong HERE,
     because the walk resets to step 1 after submitting, so the refreshed page
     shows "Request received" sitting above an empty form as though the message
     belonged to the blank one in front of you.

     replaceState so the flash stays on screen for the person who just sent the
     request — nothing re-renders — while a refresh gets a clean page. Same
     idiom as the variant URL in product.js.

     DELETE ONLY THE ONE PARAM, never rebuild the query: `preview_theme_id` is
     usually alongside it, and dropping that would throw a reviewer out of the
     draft and onto the LIVE theme without saying so. */
  (function clearPostedFlag() {
    if (!root.querySelector('.ef-ttc__flash--ok')) return;
    if (!window.history || !window.history.replaceState || !window.URL) return;
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) return;
      url.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}
  })();

  showStep(0);
})();

/* ==========================================================================
   Isometric preview — the left column renders the configuration live.

   Not Three.js, deliberately: the camera never moves, so "3D" is a fixed
   isometric projection and every face is an SVG polygon shaded from its
   outward normal. Zero dependencies; the pop-in rides --ef-duration so
   reduced-motion is honoured for free.

   Studio look (20 Aug, client's word): white ground, soft elliptical drop
   shadow, no scene backdrop. The cross-section is a 12-gon ELLIPSE, not a
   hexagon — and it grows more elliptical as the width goes up: Small is
   nearly round, Jumbo is a wide oval. Height stays the client's block
   language (3 / 4 / 5 blocks). Below the customisable body: a fixed neck
   taper and stem, then the plug assembly, coloured by the plug choice.

   Transom raises a backing wall (the tubes bolt to the boat's transom);
   Modular sits on a freestanding rack. Real product difference — keep it.
   ========================================================================== */
(function () {
  'use strict';

  var host = document.querySelector('[data-ttc-scene]');
  var form = document.querySelector('[data-ef-ttc] form');
  if (!host || !form) return;

  var SQ3 = Math.sqrt(3) / 2;
  var NS = 'http://www.w3.org/2000/svg';

  /* Per-row ground-Y offset for the square layout. iso() is linear, so
     adding it here shifts a whole tube — pump, ribs, bolts, bounds — as one
     rigid move, without threading a cy through every draw call. Set per tube
     in the render loop, zero everywhere else. */
  var Y_OFF = 0;

  function iso(x, y, z) {
    y += Y_OFF;
    return [(x - y) * SQ3, (x + y) * 0.5 - z];
  }

  /* ---- colour ---------------------------------------------------------- */

  function rgb(hexStr) {
    var h = hexStr.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }

  function mix(c1, c2, t) {
    var a = rgb(c1), b = rgb(c2);
    return 'rgb(' + Math.round(a[0] + (b[0] - a[0]) * t) + ',' +
      Math.round(a[1] + (b[1] - a[1]) * t) + ',' +
      Math.round(a[2] + (b[2] - a[2]) * t) + ')';
  }

  var TUBE_COLORS = {
    blue:  '#2456A6',
    green: '#1E7A46',
    white: '#DDE3E9',
    black: '#3A3F46',
  };
  var WATER = '#21CAC2';
  var STEEL = '#6B7280';
  var PLUG_COLORS = {
    hubbell: '#D9A514',
    alligator: '#33383F',
    crystal: '#E9EDF2',
    cannon: '#C22B2B',
  };

  /* Boxes keep three flat tones. */
  function faceShades(base) {
    return {
      top: mix(base, '#FFFFFF', 0.30),
      left: mix(base, '#FFFFFF', 0.06),
      front: mix(base, '#000000', 0.16),
      right: mix(base, '#000000', 0.34),
      line: mix(base, '#000000', 0.45),
    };
  }

  /* Curved surfaces get a brightness graded by the face normal against a
     light from the upper left — 12 facets read as a low-poly cylinder. */
  var LX = -0.507, LY = 0.862;
  function sideFill(base, nx, ny) {
    var len = Math.sqrt(nx * nx + ny * ny) || 1;
    var t = (nx * LX + ny * LY) / len;
    return t >= 0
      ? mix(base, '#FFFFFF', 0.06 + 0.26 * t)
      : mix(base, '#000000', 0.10 + 0.30 * -t);
  }

  /* ---- geometry -------------------------------------------------------- */

  var bounds;
  function pt(p) {
    if (p[0] < bounds[0]) bounds[0] = p[0];
    if (p[1] < bounds[1]) bounds[1] = p[1];
    if (p[0] > bounds[2]) bounds[2] = p[0];
    if (p[1] > bounds[3]) bounds[3] = p[1];
    return p[0].toFixed(1) + ',' + p[1].toFixed(1);
  }

  function poly(parent, pts3d, fill, stroke) {
    var el = document.createElementNS(NS, 'polygon');
    el.setAttribute('points', pts3d.map(pt).join(' '));
    el.setAttribute('fill', fill);
    if (stroke) {
      el.setAttribute('stroke', stroke);
      el.setAttribute('stroke-width', '1');
      el.setAttribute('stroke-linejoin', 'round');
    }
    parent.appendChild(el);
    return el;
  }

  /* Hexagonal again — the 12-gon ellipse pass was tried on 20 Aug and the
     client preferred the hex look. The ellipse plumbing (rx/ry everywhere)
     stays, so trying it again is a two-line change: SIDES and the radii. */
  var SIDES = 6;
  function ellPlan(cx, cy, rx, ry) {
    var pts = [], step = 360 / SIDES;
    for (var i = 0; i < SIDES; i++) {
      var a = Math.PI / 180 * (step * i + 30);
      pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
    }
    return pts;
  }

  /* An elliptical prism segment. The outward normal of an ellipse face is
     (mx/rx^2, my/ry^2), not mid-minus-centre — with a stretched plan the
     naive normal leans wrong and the shading with it. */
  function prismE(g, cx, cy, rx, ry, z0, z1, base, line, withTop, waterTop) {
    var plan = ellPlan(cx, cy, rx, ry);
    for (var i = 0; i < SIDES; i++) {
      var v1 = plan[i], v2 = plan[(i + 1) % SIDES];
      var mx = (v1[0] + v2[0]) / 2 - cx, my = (v1[1] + v2[1]) / 2 - cy;
      var nx = mx / (rx * rx), ny = my / (ry * ry);
      if (nx + ny <= 0) continue;
      poly(g,
        [iso(v1[0], v1[1], z0), iso(v2[0], v2[1], z0), iso(v2[0], v2[1], z1), iso(v1[0], v1[1], z1)],
        sideFill(base, nx, ny), line);
    }
    if (withTop) {
      poly(g, plan.map(function (v) { return iso(v[0], v[1], z1); }), mix(base, '#FFFFFF', 0.30), line);
      if (waterTop) {
        var inner = ellPlan(cx, cy, rx * 0.68, ry * 0.68);
        poly(g, inner.map(function (v) { return iso(v[0], v[1], z1); }),
          mix(WATER, '#000000', 0.12), mix(WATER, '#000000', 0.4));
      }
    }
  }

  /* Frustum between two ellipses — the fixed neck taper. */
  function frustumE(g, cx, cy, rx0, ry0, rx1, ry1, z0, z1, base, line) {
    var lo = ellPlan(cx, cy, rx0, ry0), hi = ellPlan(cx, cy, rx1, ry1);
    for (var i = 0; i < SIDES; i++) {
      var a = lo[i], b = lo[(i + 1) % SIDES], c = hi[(i + 1) % SIDES], d = hi[i];
      var mx = (a[0] + b[0] + c[0] + d[0]) / 4 - cx, my = (a[1] + b[1] + c[1] + d[1]) / 4 - cy;
      var rxm = (rx0 + rx1) / 2, rym = (ry0 + ry1) / 2;
      var nx = mx / (rxm * rxm), ny = my / (rym * rym);
      if (nx + ny <= 0) continue;
      poly(g, [iso(a[0], a[1], z0), iso(b[0], b[1], z0), iso(c[0], c[1], z1), iso(d[0], d[1], z1)],
        sideFill(base, nx, ny), line);
    }
  }

  /* Open top, like the client's render: a rim ring, dark inner cavity
     walls, and the water surface sitting 10 units down inside the bore. */
  function rimTop(g, cx, cy, rx, ry, zTop, base, line) {
    var outer = ellPlan(cx, cy, rx, ry);
    var inner = ellPlan(cx, cy, rx * 0.72, ry * 0.72);
    var zW = zTop - 10;
    for (var i = 0; i < SIDES; i++) {
      var v1 = inner[i], v2 = inner[(i + 1) % SIDES];
      var mx = (v1[0] + v2[0]) / 2 - cx, my = (v1[1] + v2[1]) / 2 - cy;
      /* back faces only — through the opening you see the FAR wall's inside */
      if (mx / (rx * rx) + my / (ry * ry) > 0) continue;
      poly(g, [iso(v1[0], v1[1], zW), iso(v2[0], v2[1], zW),
               iso(v2[0], v2[1], zTop), iso(v1[0], v1[1], zTop)],
        mix(base, '#000000', 0.52), mix(base, '#000000', 0.6));
    }
    poly(g, inner.map(function (v) { return iso(v[0], v[1], zW); }),
      mix(WATER, '#000000', 0.15), mix(WATER, '#000000', 0.4));
    for (var j = 0; j < SIDES; j++) {
      var o1 = outer[j], o2 = outer[(j + 1) % SIDES];
      var i1 = inner[j], i2 = inner[(j + 1) % SIDES];
      poly(g, [iso(o1[0], o1[1], zTop), iso(o2[0], o2[1], zTop),
               iso(i2[0], i2[1], zTop), iso(i1[0], i1[1], zTop)],
        mix(base, '#FFFFFF', 0.30), line);
    }
  }

  function line2(parent, p1, p2, color) {
    var el = document.createElementNS(NS, 'line');
    el.setAttribute('x1', p1[0].toFixed(1));
    el.setAttribute('y1', p1[1].toFixed(1));
    el.setAttribute('x2', p2[0].toFixed(1));
    el.setAttribute('y2', p2[1].toFixed(1));
    el.setAttribute('stroke', color);
    el.setAttribute('stroke-width', '2.5');
    el.setAttribute('stroke-linecap', 'round');
    pt(p1); pt(p2);
    parent.appendChild(el);
  }

  function dot(parent, p, color) {
    var el = document.createElementNS(NS, 'circle');
    el.setAttribute('cx', p[0].toFixed(1));
    el.setAttribute('cy', p[1].toFixed(1));
    el.setAttribute('r', '2.2');
    el.setAttribute('fill', color);
    pt([p[0] - 3, p[1] - 3]); pt([p[0] + 3, p[1] + 3]);
    parent.appendChild(el);
  }

  function box(g, x0, y0, z0, x1, y1, z1, sh) {
    poly(g, [iso(x1, y0, z0), iso(x1, y1, z0), iso(x1, y1, z1), iso(x1, y0, z1)], sh.right, sh.line);
    poly(g, [iso(x0, y1, z0), iso(x1, y1, z0), iso(x1, y1, z1), iso(x0, y1, z1)], sh.front, sh.line);
    poly(g, [iso(x0, y0, z1), iso(x1, y0, z1), iso(x1, y1, z1), iso(x0, y1, z1)], sh.top, sh.line);
  }

  /* ---- the scene ------------------------------------------------------- */

  var BLOCKS = { '15': 3, '17.75': 4, '22': 5 };
  /* Width drives the hex radius. */
  var ELL = {
    small:  { rx: 17, ry: 17 },
    medium: { rx: 23, ry: 23 },
    jumbo:  { rx: 30, ry: 30 },
  };
  var BLOCK_H = 30;
  var PUMP_H = 14, STEM_H = 14, NECK_H = 18;
  var ASM_H = PUMP_H + STEM_H + NECK_H;

  var prevKey = null;
  var prevTubes = 0;

  function checked(key) {
    var r = form.querySelector('input[data-key="' + key + '"]:checked');
    return r ? r.getAttribute('data-value') : null;
  }

  function render() {
    var mount = checked('mount') || 'transom';
    var tubes = parseInt(checked('tubes'), 10) || 3;
    var legal = { transom: ['17.75', '22'], modular: ['15', '17.75'] }[mount];
    var height = checked('height');
    if (!height || legal.indexOf(height) === -1) height = legal[0];
    var width = checked('width') || 'medium';
    if (width === 'small' && !(mount === 'transom' && height === '17.75')) width = 'medium';
    var color = checked('color') || 'blue';
    var plugs = checked('plugs');
    /* Square only exists on a modular 4 or 6 — anything else draws a line,
       whatever a stale radio says. Matches the form gating. */
    var layout = checked('layout') || 'line';
    if (!(mount === 'modular' && (tubes === 4 || tubes === 6))) layout = 'line';

    var key = [mount, tubes, height, width, color, plugs, layout].join('|');
    if (key === prevKey) return;
    var tubesAdded = tubes > prevTubes && prevKey !== null &&
      prevKey.split('|')[0] === mount;

    bounds = [1e9, 1e9, -1e9, -1e9];
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('aria-hidden', 'true');

    var rx = ELL[width].rx, ry = ELL[width].ry;
    var blocks = BLOCKS[height];
    var totalH = blocks * BLOCK_H;
    var spacing = rx * 2 + 16;
    /* Square: columns along X as before, a second row behind in ground-Y. */
    var cols = layout === 'square' ? tubes / 2 : tubes;
    var rows = layout === 'square' ? 2 : 1;
    var rowD = ry * 2 + 16;
    var rowW = (cols - 1) * spacing;
    var x0 = -rowW / 2;

    var base = TUBE_COLORS[color];
    var line = mix(base, '#000000', 0.45);
    var steel = faceShades(STEEL);
    var dark = faceShades('#1C2733');
    var groundZ = mount === 'modular' ? -18 : -14;

    /* soft elliptical drop shadow — the only "ground" on the white studio */
    var defs = document.createElementNS(NS, 'defs');
    var grad = document.createElementNS(NS, 'radialGradient');
    grad.setAttribute('id', 'ttcShad');
    [['0', '0.26'], ['0.55', '0.13'], ['1', '0']].forEach(function (st) {
      var stop = document.createElementNS(NS, 'stop');
      stop.setAttribute('offset', st[0]);
      stop.setAttribute('stop-color', 'rgb(16,51,87)');
      stop.setAttribute('stop-opacity', st[1]);
      grad.appendChild(stop);
    });
    defs.appendChild(grad);
    svg.appendChild(defs);

    var sc = iso(0, 0, groundZ);
    var shRx = (rowW / 2 + rx + (mount === 'transom' ? 46 : 30) + (rows - 1) * rowD / 2) * SQ3 + 26;
    var shRy = Math.max(30, shRx * 0.30);
    var shadow = document.createElementNS(NS, 'ellipse');
    shadow.setAttribute('cx', sc[0].toFixed(1));
    shadow.setAttribute('cy', (sc[1] + 6).toFixed(1));
    shadow.setAttribute('rx', shRx.toFixed(1));
    shadow.setAttribute('ry', shRy.toFixed(1));
    shadow.setAttribute('fill', 'url(#ttcShad)');
    svg.appendChild(shadow);
    pt([sc[0] - shRx, sc[1] - shRy]);
    pt([sc[0] + shRx, sc[1] + shRy + 6]);

    /* mount structure */
    if (mount === 'transom') {
      box(svg, x0 - rx - 34, -ry - 22, -14, x0 + rowW + rx + 34, -ry - 8, ASM_H + totalH * 0.9, dark);
    } else {
      var rackDy = (rows - 1) * rowD / 2;
      box(svg, x0 - rx - 18, -ry - 16 - rackDy, -18, x0 + rowW + rx + 18, ry + 16 + rackDy, 0, steel);
    }

    /* Sea Suckers are an ADD-ON and are NOT drawn — see the same note in
       tuna-scene.js. They used to paint two #0F1720 prisms here, matching the
       3D pads, and both are gone. */

    /* tubes — pump assembly, stem, fixed neck, then the customisable body */
    /* The plug choice is sold separately and does NOT appear in the model —
       the cap is simply red, as the product ships. Matches tuna-scene.js. */
    var plugSh = faceShades(PLUG_COLORS.cannon);
    var pumpSh = faceShades('#EDEFF2');
    for (var i = 0; i < tubes; i++) {
      /* Row 0 is the back row (negative Y_OFF) and is drawn first, so the
         front row paints over it — this loop is the painter's algorithm. */
      Y_OFF = (Math.floor(i / cols) - (rows - 1) / 2) * rowD;
      var g = document.createElementNS(NS, 'g');
      if (tubesAdded && i >= prevTubes) g.setAttribute('class', 'ttc-pop');
      var cx = x0 + (i % cols) * spacing;

      /* pump like the render: white body, tube-colour inlet cap on one
         side, the PLUG-colour cap on the other — the part that answers
         the plug choice */
      box(g, cx - 9, -6, 0, cx + 9, 6, PUMP_H, pumpSh);
      box(g, cx - 16, -5, 1, cx - 9, 5, PUMP_H - 1, faceShades(base));
      box(g, cx + 9, -5, 1, cx + 17, 5, PUMP_H - 1, plugSh);

      /* narrow stem and neck taper — tube-coloured, as in the render */
      prismE(g, cx, 0, rx * 0.30, ry * 0.30, PUMP_H, PUMP_H + STEM_H, base, line, false, false);
      frustumE(g, cx, 0, rx * 0.30, ry * 0.30, rx, ry, PUMP_H + STEM_H, ASM_H, base, line);

      /* body blocks; the top one opens into the rim + water bore */
      for (var b = 0; b < blocks; b++) {
        prismE(g, cx, 0, rx, ry, ASM_H + b * BLOCK_H, ASM_H + (b + 1) * BLOCK_H, base, line,
          false, false);
      }
      rimTop(g, cx, 0, rx, ry, ASM_H + blocks * BLOCK_H, base, line);

      /* X-brace ribbing and bolt dots on the two camera-facing faces —
         every block except the top collar, like the render's pattern */
      var plan = ellPlan(cx, 0, rx, ry);
      var faces = [];
      for (var fi = 0; fi < SIDES; fi++) {
        var fv1 = plan[fi], fv2 = plan[(fi + 1) % SIDES];
        var fmx = (fv1[0] + fv2[0]) / 2 - cx, fmy = (fv1[1] + fv2[1]) / 2;
        var fw = fmx / (rx * rx) + fmy / (ry * ry);
        if (fw > 0) faces.push({ v1: fv1, v2: fv2, w: fw });
      }
      faces.sort(function (a, b2) { return b2.w - a.w; });
      var xCol = mix(base, '#000000', 0.26);
      for (var xb = 0; xb < blocks - 1; xb++) {
        var zb0 = ASM_H + xb * BLOCK_H + 5, zb1 = ASM_H + (xb + 1) * BLOCK_H - 5;
        faces.slice(0, 2).forEach(function (f) {
          var pA = [f.v1[0] + (f.v2[0] - f.v1[0]) * 0.2, f.v1[1] + (f.v2[1] - f.v1[1]) * 0.2];
          var pB = [f.v1[0] + (f.v2[0] - f.v1[0]) * 0.8, f.v1[1] + (f.v2[1] - f.v1[1]) * 0.8];
          line2(g, iso(pA[0], pA[1], zb0), iso(pB[0], pB[1], zb1), xCol);
          line2(g, iso(pB[0], pB[1], zb0), iso(pA[0], pA[1], zb1), xCol);
        });
      }
      var fm = faces[0];
      var dm = [(fm.v1[0] + fm.v2[0]) / 2, (fm.v1[1] + fm.v2[1]) / 2];
      for (var db = 1; db < blocks; db++) {
        dot(g, iso(dm[0], dm[1], ASM_H + db * BLOCK_H), mix(base, '#000000', 0.5));
      }

      svg.appendChild(g);
    }
    Y_OFF = 0;

    var pad = 26;
    svg.setAttribute('viewBox',
      (bounds[0] - pad).toFixed(0) + ' ' + (bounds[1] - pad).toFixed(0) + ' ' +
      (bounds[2] - bounds[0] + pad * 2).toFixed(0) + ' ' +
      (bounds[3] - bounds[1] + pad * 2).toFixed(0));

    var waves = host.querySelector('.ef-ttc__waves');
    if (waves) waves.remove();
    var old = host.querySelector('svg');
    if (old) old.remove();
    var ph = host.querySelector('[data-ttc-placeholder]');
    if (ph) ph.remove();
    if (!tubesAdded && prevKey !== null) svg.setAttribute('class', 'ttc-swap');
    host.appendChild(svg);

    prevKey = key;
    prevTubes = tubes;
  }

  function boot() {
    form.addEventListener('change', render);
    render();
  }

  /* The 3D scene takes precedence when the section asks for it AND WebGL is
     available. It sets `active` once it has painted a frame, and calls
     `fallback()` if the library will not load or the scene throws — so a
     broken 3D path degrades to this renderer instead of a blank white plate.
     The timeout covers tuna-scene.js never executing at all. */
  window.EFTunaSVG = { fallback: boot, active: false };

  if (!window.__ttcPrefer3D) {
    boot();
  } else {
    setTimeout(function () {
      if (!window.EFTunaSVG.active) boot();
    }, 5000);
  }
})();
